import { useEffect, useMemo, useState } from "react";
import PropertyImage from "../PropertyImage";
import PropertyImagePlaceholder from "../PropertyImagePlaceholder";
import { resolvePublicImageUrl } from "../../../utils/resolvePublicImageUrl";

const HERO_THUMB_LIMIT = 4;

const PropertyImageGallery = ({ property, gallery = [], primaryImage = null }) => {
  const items = useMemo(() => {
    const list = gallery?.length ? gallery : primaryImage ? [primaryImage] : [];
    return list.filter((item) => item?.url);
  }, [gallery, primaryImage]);

  const initialId =
    primaryImage?.galleryImageId ||
    primaryImage?.imageId ||
    items.find((item) => item.isPrimary)?.galleryImageId ||
    items.find((item) => item.isPrimary)?.imageId ||
    items[0]?.galleryImageId ||
    items[0]?.imageId;

  const [selectedId, setSelectedId] = useState(initialId);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setSelectedId(initialId);
    setShowAll(false);
  }, [property?.id, initialId]);

  const selected =
    items.find(
      (item) => item.galleryImageId === selectedId || item.imageId === selectedId
    ) || items[0];

  const heroThumbs = items.slice(0, HERO_THUMB_LIMIT);
  const hasMore = items.length > HERO_THUMB_LIMIT;

  if (!items.length) {
    return (
      <div className="property-detail-gallery property-detail-gallery--empty" aria-label="Property photos">
        <PropertyImagePlaceholder label="Photos coming soon" />
      </div>
    );
  }

  const attribution =
    selected?.attribution || selected?.sourceName || property?.primarySourceName;
  const altText = selected?.alt || property?.title || "Property photo";

  return (
    <section className="property-detail-gallery" aria-label="Property photo gallery">
      <div className="property-detail-gallery__hero">
        <PropertyImage
          src={selected?.url}
          className="property-detail-gallery__main-image"
          alt={altText}
          imageId={selected?.imageId}
          canonicalPropertyId={property?.id || property?.canonicalListingId}
          sourceKey={selected?.sourceKey || property?.primaryImageSourceKey}
          sourceListingId={selected?.sourceListingId}
          isCached={selected?.isCached}
          cacheStatus={selected?.cacheStatus}
          renderContext="PROPERTY_DETAIL_PAGE"
          pageContext="PROPERTY_DETAIL"
        />
        {items.length > 1 ? (
          <span className="property-detail-gallery__count">{items.length} photos</span>
        ) : null}
      </div>

      {attribution ? (
        <p className="property-detail-gallery__attribution">
          {selected?.isCached ? "Image via " : "Photo from "}
          {attribution}
        </p>
      ) : null}

      {heroThumbs.length > 1 ? (
        <div className="property-detail-gallery__thumbs" role="list" aria-label="Photo thumbnails">
          {heroThumbs.map((item) => {
            const thumbId = item.galleryImageId || item.imageId;
            const isActive =
              thumbId === (selected?.galleryImageId || selected?.imageId);
            const thumbSrc = resolvePublicImageUrl(item.thumbnailUrl || item.url);

            return (
              <button
                key={thumbId}
                type="button"
                role="listitem"
                className={`property-detail-gallery__thumb${isActive ? " is-active" : ""}`}
                onClick={() => setSelectedId(thumbId)}
                aria-label={`View photo ${item.position || ""}`}
                aria-pressed={isActive}
              >
                <img src={thumbSrc} alt="" loading="lazy" />
              </button>
            );
          })}
        </div>
      ) : null}

      {hasMore ? (
        <button
          type="button"
          className="property-detail-gallery__view-all"
          onClick={() => setShowAll((value) => !value)}
          aria-expanded={showAll}
        >
          {showAll ? "Show fewer photos" : `View all ${items.length} photos`}
        </button>
      ) : null}

      {showAll && hasMore ? (
        <div className="property-detail-gallery__grid" role="list" aria-label="All property photos">
          {items.map((item) => {
            const thumbId = item.galleryImageId || item.imageId;
            const isActive =
              thumbId === (selected?.galleryImageId || selected?.imageId);

            return (
              <button
                key={`all-${thumbId}`}
                type="button"
                role="listitem"
                className={`property-detail-gallery__grid-item${isActive ? " is-active" : ""}`}
                onClick={() => setSelectedId(thumbId)}
                aria-pressed={isActive}
              >
                <img
                  src={resolvePublicImageUrl(item.thumbnailUrl || item.url)}
                  alt=""
                  loading="lazy"
                />
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
};

export default PropertyImageGallery;
